import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const EngagementManagement = () => {
  const [openCategory, setOpenCategory] = useState('A');

  const rules = [
    { label: 'Response Time', val: '1-2 Hours (Business)', icon: 'Clock' },
    { label: 'Priority', val: 'DMs > Comments > Tags', icon: 'AlertCircle' },
    { label: 'Tone', val: 'Aligned & Professional', icon: 'Mic' },
    { label: 'Conflict', val: 'No Arguing - Escalate', icon: 'Shield' },
  ];

  const triage = {
    'A': { title: 'Leads (Category A)', color: 'bg-apache/10 text-apache', borderColor: 'border-apache', items: ['Business Inquiries', 'Partnerships', 'Media Requests'], action: 'Respond < 1hr & Forward' },
    'B': { title: 'Questions (Category B)', color: 'bg-gold/10 text-brown', borderColor: 'border-gold', items: ['Product Info', 'How-to', 'Availability'], action: 'Respond < 2hr with Info' },
    'C': { title: 'General (Category C)', color: 'bg-beige text-brown/70', borderColor: 'border-brown/20', items: ['Compliments', 'Fan Messages', 'Emojis'], action: 'Respond < 4hr with Appreciation' }
  };

  const macros = [
    { title: 'Appreciation', text: "Thank you so much for the kind words! We really appreciate your support." },
    { title: 'Product Inquiry', text: "Thanks for your interest! You can find more details at [Link]. DM us if you have specific questions!" },
    { title: 'Customer Service', text: "We want to resolve this quickly. Please DM us your order details so we can help." },
    { title: 'Partnership', text: "Thanks for reaching out! Please send your proposal to partnerships@[brand].com." }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="max-w-7xl mx-auto"
    >
      <h1 className="section-title">Engagement & Community</h1>

      {/* Rulebook Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {rules.map((r, i) => (
          <motion.div 
            key={i}
            whileHover={{ y: -5 }}
            className="bg-white p-4 rounded-xl shadow-sm border border-gold/20 text-center"
          >
            <div className="mx-auto w-10 h-10 bg-beige rounded-full flex items-center justify-center mb-3 text-gold">
              <SafeIcon name={r.icon} />
            </div>
            <h3 className="text-xs font-bold text-brown/50 uppercase">{r.label}</h3>
            <p className="font-semibold text-brown text-sm mt-1">{r.val}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Interactive Triage */}
        <div className="section-card h-full">
          <h2 className="subsection-title">DM Triage System</h2>
          <div className="flex space-x-2 mb-4">
            {Object.keys(triage).map(cat => (
              <button
                key={cat}
                onClick={() => setOpenCategory(cat)}
                className={`flex-1 py-2 rounded-lg font-bold transition-colors ${
                  openCategory === cat 
                  ? 'bg-brown text-white' 
                  : 'bg-beige text-brown/60 hover:bg-gold/20'
                }`}
              >
                Cat {cat}
              </button>
            ))}
          </div>
          
          <AnimatePresence mode="wait">
            <motion.div
              key={openCategory}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`p-6 rounded-xl border-2 border-dashed ${triage[openCategory].borderColor} ${triage[openCategory].color}`}
            >
              <h3 className="text-lg font-bold mb-4">
                {triage[openCategory].title}
              </h3>
              <ul className="space-y-2 mb-6">
                {triage[openCategory].items.map((item, i) => (
                  <li key={i} className="flex items-center text-brown">
                    <SafeIcon name="ArrowRight" className="w-4 h-4 mr-2 opacity-50" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="bg-white/50 p-3 rounded-lg text-sm font-medium shadow-sm border border-black/5">
                <span className="text-brown/40 uppercase text-xs block mb-1">Protocol</span>
                {triage[openCategory].action}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Macro Cards */}
        <div className="section-card h-full bg-beige/30">
          <h2 className="subsection-title">Quick Response Macros</h2>
          <div className="space-y-3 overflow-y-auto max-h-[400px] pr-2">
            {macros.map((m, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-4 rounded-lg shadow-sm border border-gold/10 cursor-pointer group"
                whileHover={{ scale: 1.02 }}
              >
                <h4 className="text-sm font-bold text-gold mb-2 uppercase group-hover:text-brown transition-colors">
                  {m.title}
                </h4>
                <p className="text-brown/80 text-sm italic">"{m.text}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EngagementManagement;